import React from 'react';
import { ISummaryPageSettings } from '../../../models/summary/ISummaryPageSettings';
import { ISummaryData } from '../../../models/summary/ISummaryData';
import SummaryDegree from '../../../components/molecules/summary-degree/summary-degree';
import styles from './summary-degree-list.module.scss';
import { useEffect, useState } from 'react';
import { ISummaryCourse } from '../../../models/summary/ISummaryCourse';
import { ICourseInstance } from '../../../models/course/ICourseData';
import { getBrandProgramInstancesAsync } from '../../../api-clients/catalog-service-client';
import LoadingSpinner from '../../../components/atoms/loading-spinner/loading-spinner';
import { StudentType } from '../../../../src/constants/student-type';

type Props = {
  degrees: ISummaryData[];
  summaryPageSettings: ISummaryPageSettings;
  pagingLimit: number;
  brandName: string;
  studentLevel?: StudentType;
  application: string;
  googleTagId?: string;
  courseList?: ISummaryCourse[];
};
export default function SummaryDegreeList({
  degrees,
  pagingLimit,
  summaryPageSettings,
  brandName,
  studentLevel,
  application,
  googleTagId,
  courseList,
}: Props) {
  const [searchInput, setSearchInput] = useState('');
  const [scrollValue, setScrollValue] = useState<number>(0);
  const [isDegreesLoading, setIsDegreesLoading] = useState<boolean>(false);

  const [degreeSourceList, setDegreeSourceList] = useState<ISummaryData[]>([]);
  const [degreeList, setDegreeList] = useState<ISummaryData[]>([]);

  const [cachedInstances, setCachedInstances] = useState<Map<string, ICourseInstance[]>>(new Map());

  const handleChange = (e) => {
    e.preventDefault();
    setSearchInput(e.target.value);
  };

  const onScroll = (event) => {
    const {
      target: { body, documentElement },
    } = event;
    const scrollTop = body.scrollTop || documentElement.scrollTop;
    const height = documentElement.scrollHeight - documentElement.clientHeight;
    const scrolled = scrollTop / height;
    setScrollValue(scrolled);
  };

  useEffect(() => {
    updateDegreeSourceList(searchInput, studentLevel);
  }, [searchInput, studentLevel]);

  useEffect(() => {
    const fetchData = async () => {
      setDegreeList(await getDegreesWithInstances(degreeSourceList.slice(0, pagingLimit)));
    };

    fetchData().catch(console.error);
  }, [degreeSourceList]);

  useEffect(() => {
    const length = degreeList != null ? degreeList?.length : 0;
    if (scrollValue > 0.95 && length < degrees.length && !isDegreesLoading) {
      loadDegreeList();
    }
  }, [scrollValue]);

  useEffect(() => {
    window.addEventListener(`scroll`, onScroll);

    return () => {
      window.removeEventListener(`scroll`, onScroll);
    };
  }, []);

  const updateDegreeSourceList = (searchValue: string, studentLevelValue: string) => {
    let filteredDegrees = studentLevelValue
      ? degrees.filter((x) =>
          x.studentType?.some((y) => y.toLowerCase() === studentLevelValue.toLowerCase()),
        )
      : degrees;

    filteredDegrees =
      searchValue.length > 0
        ? filteredDegrees.filter((degree) =>
            degree.name.toLowerCase().match(searchInput.toLowerCase()),
          )
        : filteredDegrees;

    setDegreeSourceList(filteredDegrees);
  };

  const loadDegreeList = async () => {
    setIsDegreesLoading(true);

    const length = degreeList != null ? degreeList?.length : 0;
    const progressionRemainingLength = degreeList?.length + pagingLimit;
    const remainingProgression = Array.from(degreeSourceList).slice(
      length,
      progressionRemainingLength,
    );

    const remainingProgressionWithInstances = await getDegreesWithInstances(remainingProgression);

    setDegreeList([...degreeList, ...remainingProgressionWithInstances]);

    setIsDegreesLoading(false);
  };

  const getDegreesWithInstances = async (degrees: ISummaryData[]) => {
    await Promise.all(
      degrees.map(async (degree) => {
        degree.instances = await getInstances(degree);
      }),
    );

    return degrees;
  };

  // Get instnces from cache and turn it back. If cache doesn't contains needed instances, it make request
  // to the catalog service, put responce into the cache and turn it back
  const getInstances = async (degree: ISummaryData): Promise<ICourseInstance[]> => {
    const fromCache = cachedInstances.get(degree.id);
    if (fromCache) return fromCache;

    const response = await getBrandProgramInstancesAsync(
      degree.brandId,
      degree.id,
      {
        baseURL: summaryPageSettings.catalogServiceConfig.urlV1,
        locale: summaryPageSettings.catalogServiceConfig.locale,
      },
      summaryPageSettings.catalogServiceConfig.includePrerelease,
    ).catch((e) => ({ data: [] }));

    if (!response || !response.data) {
      cachedInstances.set(degree.id, []);
      return [];
    }

    cachedInstances.set(degree.id, response.data);

    return response.data;
  };

  return (
    <div>
      <div className='row'>
        <div className='col-md-6 mb-3'>
          <input
            type='search'
            className={styles[`search-box`]}
            onChange={handleChange}
            value={searchInput}
            placeholder='Start typing program name'
            aria-label='start typing here to search for your desired program'
          />
        </div>
      </div>
      <div className={styles[`degree-list`]}>
        {degreeList?.length > 0
          ? degreeList.map((degree) => (
              <SummaryDegree
                key={degree.id}
                degree={degree}
                summaryPageSettings={summaryPageSettings}
                brandName={brandName}
                googleTagId={googleTagId}
                courseList={courseList}
                application={application}
                studentLevel={studentLevel}
              />
            ))
          : !isDegreesLoading && (
              <div className='mt-4'>
                <p>No result found, please search again</p>
              </div>
            )}
      </div>
      {isDegreesLoading && (
        <LoadingSpinner
          spinnerId='loading-spinner-degrees'
          src='/public/next-assets/loaders/into.svg'
          width={64}
          height={64}
        />
      )}
    </div>
  );
}
